/**
 * Modal and Drawer — the only two floating dialogs in the app.
 *
 * WHY THIS FILE IS SHARED
 * -----------------------
 * The repo previously had four hand-rolled overlays (CallDetailsModal,
 * ConfirmModal, ReportGenerationModal, LeadDetailDrawer). None of them trapped
 * Tab, two of them left `document.body.style.overflow = 'hidden'` behind after
 * unmount, and every one of them stole focus without giving it back — so closing
 * a modal dropped keyboard users at the top of the document. All of that is
 * fixed once, here.
 *
 * NESTING
 * -------
 * A drawer can open a confirm modal on top of itself, so the state that must be
 * global — "which dialog owns Escape" and "who unlocks body scroll" — lives in a
 * module-level stack rather than in each component. Only the topmost dialog
 * responds to Escape; scroll is unlocked only when the last dialog leaves.
 */

import React, { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';

import { IconButton } from './Button';

/* ─── The dialog stack ───────────────────────────────────────────────────── */

/** Open dialogs, oldest first. Identity tokens, not nodes. */
const stack = [];

/** The body style we displaced, captured once when the first dialog opened. */
let bodyLock = null;

const lockBodyScroll = () => {
  const { body } = document;
  bodyLock = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
  // Compensate for the scrollbar we are about to remove, otherwise the whole
  // page jumps sideways by 15px as the dialog opens.
  const gutter = window.innerWidth - document.documentElement.clientWidth;
  body.style.overflow = 'hidden';
  if (gutter > 0) body.style.paddingRight = `${gutter}px`;
};

const unlockBodyScroll = () => {
  if (!bodyLock) return;
  // Restore what was there before — not `''`. A page may legitimately own
  // body overflow for its own reasons.
  document.body.style.overflow = bodyLock.overflow;
  document.body.style.paddingRight = bodyLock.paddingRight;
  bodyLock = null;
};

const pushDialog = (token) => {
  stack.push(token);
  if (stack.length === 1) lockBodyScroll();
};

const popDialog = (token) => {
  const at = stack.indexOf(token);
  if (at !== -1) stack.splice(at, 1);
  if (stack.length === 0) unlockBodyScroll();
};

const isTopmost = (token) => stack[stack.length - 1] === token;

/* ─── Focus ─────────────────────────────────────────────────────────────── */

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Tabbable descendants in DOM order. Rendered-but-invisible nodes (a collapsed
 * section, a `hidden` input) are excluded, because tabbing to something the user
 * cannot see is indistinguishable from focus disappearing.
 */
const focusableWithin = (root) => {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) =>
      el.tabIndex !== -1 &&
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.getClientRects().length > 0,
  );
};

/**
 * Layers that belong to the dialog but live in their own portal, so they are not
 * DOM descendants of the panel: a Select's listbox, an InfoTip's bubble. Focus
 * inside one of these is legitimate — dragging it back to the panel would make
 * an open listbox unusable.
 */
const FLOATING_LAYER = '[role="listbox"], [role="menu"], [role="tooltip"], [role="dialog"]';

/**
 * A companion surface that stays usable while a dialog is open: the assistant
 * panel. Focus and keys inside it belong to it, not to the dialog underneath —
 * otherwise opening a contact would make the chat about that contact untypable.
 */
const COMPANION = '[data-assistant-panel]';
const inCompanion = (node) => Boolean(node && typeof node.closest === 'function' && node.closest(COMPANION));

const inFloatingLayer = (panel, node) => {
  if (!node || typeof node.closest !== 'function') return false;
  if (panel && panel.contains(node)) return false;
  return Boolean(node.closest(FLOATING_LAYER));
};

/** Wrap Tab / Shift+Tab at the edges of the panel. */
const trapTab = (event, panel) => {
  if (!panel) return;
  const items = focusableWithin(panel);

  if (items.length === 0) {
    // Nothing to land on — hold focus on the panel itself rather than letting
    // it escape to the page behind the scrim.
    event.preventDefault();
    panel.focus();
    return;
  }

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  const outside = !panel.contains(active) || active === panel;

  if (event.shiftKey) {
    if (outside || active === first) {
      event.preventDefault();
      last.focus();
    }
  } else if (outside || active === last) {
    event.preventDefault();
    first.focus();
  }
};

/**
 * Everything a dialog needs to behave like one: stack membership, Escape,
 * the Tab trap, body scroll lock, initial focus, and focus restoration.
 *
 * @param {object}  args
 * @param {boolean} args.open
 * @param {Function} args.onClose
 * @param {object}  args.panelRef        ref to the element carrying role="dialog"
 * @param {object=} args.initialFocusRef ref to focus on open; defaults to the
 *                                       first focusable descendant
 */
function useDialog({ open, onClose, panelRef, initialFocusRef }) {
  // Identity for this instance in the stack. An object literal is enough — it
  // only ever has to be `===` itself.
  const tokenRef = useRef(null);
  if (tokenRef.current === null) tokenRef.current = {};

  // The listeners are registered once per open, so they read onClose through a
  // ref instead of re-subscribing every time the parent re-renders.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const token = tokenRef.current;
    const restoreTo = document.activeElement;
    pushDialog(token);

    const onKeyDown = (event) => {
      if (!isTopmost(token) || event.defaultPrevented) return;
      if (inCompanion(event.target)) return;

      // Bubble phase on purpose: a listbox or tooltip inside the dialog gets
      // first refusal on Escape and closes itself by calling stopPropagation.
      // Escape should shed one layer at a time, not the whole dialog.
      if (event.key === 'Escape') {
        event.preventDefault();
        if (closeRef.current) closeRef.current();
        return;
      }
      if (event.key === 'Tab') {
        if (inFloatingLayer(panelRef.current, document.activeElement)) return;
        trapTab(event, panelRef.current);
      }
    };

    // Focus can also leave without Tab — a script calling focus(), a click on
    // the scrim, or Safari's Option+Tab. Pull it back.
    const onFocusIn = (event) => {
      if (!isTopmost(token)) return;
      const panel = panelRef.current;
      if (!panel || panel.contains(event.target)) return;
      if (inFloatingLayer(panel, event.target)) return;
      if (inCompanion(event.target)) return;
      const items = focusableWithin(panel);
      (items[0] || panel).focus();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      popDialog(token);
      // Hand focus back to whatever opened us, provided it is still on the page
      // — a row action button survives a close, but a row that was filtered
      // away by the mutation the dialog just performed does not.
      if (restoreTo && typeof restoreTo.focus === 'function' && document.contains(restoreTo)) {
        restoreTo.focus();
      }
    };
  }, [open, panelRef]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const target =
      (initialFocusRef && initialFocusRef.current) || focusableWithin(panel)[0] || panel;
    target.focus();
    // initialFocusRef is read once, at open, exactly like autoFocus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, panelRef]);
}

/* ─── Shared chrome ─────────────────────────────────────────────────────── */

// The one permitted blur in the app: the scrim, where there is no text to
// soften and the blur is what tells you the layer below is inert.
const SCRIM = 'absolute inset-0 bg-ink-900/50 backdrop-blur-[2px] animate-fade-in';

/** @param {{ onClose?: Function }} props — no handler means the scrim is inert. */
function Scrim({ onClose }) {
  if (!onClose) return <div className={SCRIM} aria-hidden="true" />;
  // A real button, per the no-clickable-div rule. It is deliberately untabbable
  // and hidden from assistive tech: Escape is the keyboard route out, and a
  // screen reader announcing "close, button" over the whole viewport is noise.
  return (
    <button
      type="button"
      aria-hidden="true"
      tabIndex={-1}
      onClick={onClose}
      className={`${SCRIM} cursor-default`}
    />
  );
}

/**
 * @param {object} props
 * @param {string} props.titleId  wired to the dialog's aria-labelledby
 * @param {string=} props.descId  wired to aria-describedby when a subtitle exists
 */
function DialogHeader({ title, subtitle, titleId, descId, onClose }) {
  return (
    <header className="flex items-start gap-gutter border-b border-line px-card py-card">
      <div className="min-w-0 flex-1">
        {title ? (
          <h2 id={titleId} className="text-title font-semibold text-ink-900">
            {title}
          </h2>
        ) : null}
        {subtitle ? (
          <p id={descId} className="mt-1 text-body text-ink-500">
            {subtitle}
          </p>
        ) : null}
      </div>
      <IconButton icon="fa-xmark" label="Close" size="sm" onClick={onClose} />
    </header>
  );
}

function DialogFooter({ children }) {
  return (
    <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-2 px-card py-gutter">
      {children}
    </footer>
  );
}

/* ─── Modal ─────────────────────────────────────────────────────────────── */

const MODAL_SIZE = {
  sm: 'max-w-sm', // 384 — one question, two buttons
  md: 'max-w-lg', // 512 — the default form
  lg: 'max-w-2xl', // 672 — a form dense enough to need two columns
  xl: 'max-w-4xl', // 896 — a transcript or an embedded table
};

/**
 * A centred dialog.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {React.ReactNode=} props.title      names the dialog for assistive tech
 * @param {React.ReactNode=} props.subtitle
 * @param {'sm'|'md'|'lg'|'xl'=} props.size
 * @param {React.ReactNode=} props.footer     actions; right-aligned on a hairline
 * @param {boolean=} props.closeOnBackdrop    false for a form with unsaved edits
 * @param {object=} props.initialFocusRef
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  children,
  closeOnBackdrop = true,
  initialFocusRef,
}) {
  const panelRef = useRef(null);
  const uid = useId();
  const titleId = `${uid}-title`;
  const descId = `${uid}-desc`;

  useDialog({ open, onClose, panelRef, initialFocusRef });

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6">
      <Scrim onClose={closeOnBackdrop ? onClose : undefined} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Dialog'}
        aria-describedby={subtitle ? descId : undefined}
        tabIndex={-1}
        className={`relative flex w-full ${
          MODAL_SIZE[size] || MODAL_SIZE.md
        } max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-modal animate-slide-up`}
      >
        <DialogHeader
          title={title}
          subtitle={subtitle}
          titleId={titleId}
          descId={descId}
          onClose={onClose}
        />
        <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto px-card py-card text-body text-ink-700">
          {children}
        </div>
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </div>
    </div>,
    document.body,
  );
}

/* ─── Drawer ────────────────────────────────────────────────────────────── */

// Below `sm` a drawer is the full width of the screen: a 384px panel on a 390px
// phone leaves a 6px strip of scrim that is impossible to hit deliberately.
const DRAWER_WIDTH = {
  sm: 'sm:max-w-sm', // 384
  md: 'sm:max-w-md', // 448
  lg: 'sm:max-w-xl', // 576 — the default: a call or lead detail panel
  xl: 'sm:max-w-3xl', // 768 — a transcript beside its metadata
};

/**
 * An edge-anchored dialog for detail-beside-context: the list stays visible
 * behind it, so the user keeps their place.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {React.ReactNode=} props.title
 * @param {React.ReactNode=} props.subtitle
 * @param {'right'|'left'=} props.side
 * @param {'sm'|'md'|'lg'|'xl'=} props.width
 * @param {React.ReactNode=} props.footer
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  side = 'right',
  width = 'lg',
  footer,
  children,
}) {
  const panelRef = useRef(null);
  const uid = useId();
  const titleId = `${uid}-title`;
  const descId = `${uid}-desc`;

  useDialog({ open, onClose, panelRef });

  if (!open) return null;

  const fromLeft = side === 'left';

  return ReactDOM.createPortal(
    // `cv-drawer-layer`: with the assistant open on a wide screen, the drawer
    // stops at the panel's edge instead of disappearing under it (index.css).
    <div className="cv-drawer-layer fixed inset-0 z-[70] flex">
      <Scrim onClose={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Dialog'}
        aria-describedby={subtitle ? descId : undefined}
        tabIndex={-1}
        className={`relative flex h-full w-full flex-col overflow-hidden bg-surface shadow-modal ${
          DRAWER_WIDTH[width] || DRAWER_WIDTH.lg
        } ${
          fromLeft
            ? // There is one slide keyframe and it travels leftward, so a
              // left-hand drawer fades instead of sliding the wrong way.
              'mr-auto border-r border-line animate-fade-in'
            : 'ml-auto border-l border-line animate-slide-in-right'
        }`}
      >
        <DialogHeader
          title={title}
          subtitle={subtitle}
          titleId={titleId}
          descId={descId}
          onClose={onClose}
        />
        <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto px-card py-card text-body text-ink-700">
          {children}
        </div>
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </div>
    </div>,
    document.body,
  );
}
